<template>
  <div>
    <b-row>
      <b-col class="mb-50">
        <h4>
          {{ $t('dashboard.teams.title') }}
        </h4>
      </b-col>
    </b-row>

    <b-row>
      <b-col
        md="6"
        cols="12"
      >

        <rc-overlay
          :show="loading"
          rounded
        >
          <statistic-card-horizontal
            icon="UsersIcon"
            :statistic="teamsStatistics.available"
            :statistic-title="$t('dashboard.teams.free')"
            color="success"
          />
        </rc-overlay>

      </b-col>

      <b-col
        md="6"
        cols="12"
      >

        <rc-overlay
          :show="loading"
          rounded
        >
          <statistic-card-horizontal
            icon="UsersIcon"
            :statistic="teamsStatistics.total - teamsStatistics.available"
            :statistic-title="$t('dashboard.teams.busy')"
            color="danger"
          />
        </rc-overlay>

      </b-col>

    </b-row>

    <b-row v-if="activeTeams.length">
      <b-col class="mb-50">
        <h4>
          {{ $t('dashboard.active-teams.title') }}
        </h4>
      </b-col>
    </b-row>

    <b-row class="match-height">
      <b-col
        v-for="team in activeTeams"
        :key="team.id"
        cols="12"
        md="6"
        xl="4"
      >
        <b-card
          class="team-card"
          :class="{
            'team-card-success': team.status,
            'team-card-danger': !team.status
          }"
        >

          <div>
            <h4 class="mb-1">
              {{ team.teamDefinition.code }}
              <b-dropdown
                v-visible-to-role="['ROLE_DISPATCHER']"
                class="float-right mt-n50 mr-n1"
                variant="flat-secondary"
                size="sm"
                right
                toggle-class="btn btn-flat-primary btn-sm"
                no-caret
              >
                <template #button-content>
                  <feather-icon icon="MoreVerticalIcon" />
                </template>
                <b-dropdown-item :to="{ name: 'team-view', params: { id: team.id } }">
                  {{ $t('shared.button.see') }}
                </b-dropdown-item>
                <b-dropdown-item @click="openPlanningModal(team.teamDefinition)">
                  {{ $t('order.planning.plan-action') }}
                </b-dropdown-item>
              </b-dropdown>
            </h4>
          </div>

          <div v-if="team.id">
            <dl
              v-if="team.paramedicUser"
              class="row mb-0"
            >
              <dt class="font-bold col-4">
                {{ $t('dashboard.teams.label.paramedic-user') }}
              </dt>
              <dd class="col-8">
                {{ team.paramedicUser.surname }} {{ team.paramedicUser.name }}
              </dd>
            </dl>
            <dl class="row mb-0">
              <dt class="font-bold col-4">
                {{ $t('dashboard.teams.label.driver-user') }}
              </dt>
              <dd class="col-8">
                {{ team.driverUser.surname }} {{ team.driverUser.name }}
              </dd>
            </dl>
            <dl class="row mb-0">
              <dt class="font-bold col-4">
                {{ $t('dashboard.teams.label.vehicle') }}
              </dt>
              <dd class="col-8">
                {{ team.vehicle.sideNumber }}
              </dd>
            </dl>
            <dl class="row mb-0">
              <dt class="font-bold col-4">
                {{ $t('dashboard.teams.label.working-from') }}
              </dt>
              <dd class="col-8">
                {{ transformDatetimeToHumanReadablePretty(team.teamStartedAt) }}
              </dd>
            </dl>
            <dl
              v-if="team.currentOrder"
              class="row mb-0"
            >
              <dt class="font-bold col-4">
                {{ $t('dashboard.teams.label.current-order') }}
              </dt>
              <dd class="col-8">
                <router-link :to="{ name: 'order-view', params: { id: team.currentOrder.id } }">
                  {{ team.currentOrder.fullNumber }}
                </router-link>
                <span>
                  <br>/&nbsp;{{ $t('order.status.' + team.currentOrder.status) }}
                </span>
              </dd>
            </dl>
            <dl
              v-if="hasPlannedOrders(team)"
              class="row mb-0"
            >
              <dt class="font-bold col-4">
                {{ $t('order.planning.planned-title') }}
                <feather-icon
                  v-b-tooltip.hover.top="$t('dashboard.teams.label.planned-tooltip')"
                  icon="HelpCircleIcon"
                  size="14"
                  class="text-muted ml-25"
                />
              </dt>
              <dd class="col-8 d-flex align-items-center flex-wrap">
                <b-badge
                  variant="light-secondary"
                  class="text-nowrap"
                >
                  {{ getFirstPlannedOrderNumber(team) }}
                </b-badge>
                <b-badge
                  v-if="getRemainingPlannedOrdersCount(team)"
                  variant="light-secondary"
                  class="text-nowrap ml-50"
                >
                  +{{ getRemainingPlannedOrdersCount(team) }}
                </b-badge>
              </dd>
            </dl>
          </div>
        </b-card>
      </b-col>
    </b-row>
    <order-planning-modal
      ref="orderPlanningModal"
      @planning-updated="handlePlanUpdated"
    />
  </div>
</template>
<script>
import StatisticCardHorizontal from '@core/components/statistics-cards/StatisticCardHorizontal.vue'
import { VBTooltip } from 'bootstrap-vue'
import PusherClient from '@/libs/pusher'
import { parseRequestError, transformDatetimeToHumanReadablePretty } from '@/helpers/helpers'
import RcOverlay from '@/layouts/components/rc/RcOverlay.vue'
import OrderPlanningModal from '@/views/orders/OrderPlanning/OrderPlanningModal.vue'
import { orderRequests } from '@/http/requests/order/order'

const PLANNED_ORDERS_PREVIEW_WINDOW_HOURS = 12
const PLANNED_ORDERS_PREVIEW_WINDOW_MS = PLANNED_ORDERS_PREVIEW_WINDOW_HOURS * 60 * 60 * 1000

export default {
  name: 'TeamStatistics',
  directives: {
    'b-tooltip': VBTooltip,
  },
  components: {
    StatisticCardHorizontal,
    RcOverlay,
    OrderPlanningModal,
  },
  data() {
    return {
      loading: true,
      teamUpdateHandler: null,
      orderUpdateHandler: null,
      plannedOrdersByTeamDefinition: {},
    }
  },
  computed: {
    teamsStatistics() {
      return { total: 0, available: 0, ...this.$store.getters['team/statisticsCurrent'] }
    },
    activeTeams() {
      return this.$store.getters['team/getActiveTeams']
    },
  },
  created() {
    this.teamUpdateHandler = this.handleRealtimeUpdate.bind(this)
    this.orderUpdateHandler = this.handleRealtimeUpdate.bind(this)
  },
  mounted() {
    this.init()
    PusherClient.bind('update', 'team', this.teamUpdateHandler, this)
    PusherClient.bind('update', 'order', this.orderUpdateHandler, this)
  },
  beforeDestroy() {
    PusherClient.unbind('update', 'team', this.teamUpdateHandler, this)
    PusherClient.unbind('update', 'order', this.orderUpdateHandler, this)
    this.$store.commit('team/SET_ACTIVE_TEAMS', [])
  },
  methods: {
    handleRealtimeUpdate() {
      this.init()
    },
    init() {
      this.loading = true
      Promise.all([
        this.$store.dispatch('team/fetchStatisticsCurrent'),
        this.$store.dispatch('team/fetchActiveTeams', { currentOnly: 1 }),
        this.fetchPlannedOrdersSummary(),
      ])
        .catch(error => {
          parseRequestError(error)
        })
        .finally(() => {
          this.loading = false
        })
    },
    async fetchPlannedOrdersSummary() {
      const response = await orderRequests.getOrders({
        plannedOnly: 1,
        plannedTeamDefinitionId: null,
        statuses: ['received'],
        limit: 1000,
        page: 1,
        sortField: 'startAt',
        sortDir: 'asc',
      })

      const items = Array.isArray(response?.data?.items) ? response.data.items : []
      this.plannedOrdersByTeamDefinition = items.reduce((accumulator, order) => {
        if (!this.isOrderWithinPlannedPreviewWindow(order)) {
          return accumulator
        }

        const teamDefinitionIds = order?.isFirstAvailablePlanning && Array.isArray(order?.plannedTeamDefinitions)
          ? order.plannedTeamDefinitions.map(td => td.id).filter(Boolean)
          : [order?.plannedTeamDefinition?.id].filter(Boolean)

        if (!teamDefinitionIds.length) {
          return accumulator
        }

        return teamDefinitionIds.reduce((acc, teamDefinitionId) => {
          const current = acc[teamDefinitionId] || []
          return {
            ...acc,
            [teamDefinitionId]: [...current, order],
          }
        }, accumulator)
      }, {})
    },
    isOrderWithinPlannedPreviewWindow(order) {
      const referenceDate = this.getPlannedOrderReferenceDate(order)
      if (!referenceDate) {
        return false
      }

      const referenceTime = referenceDate.getTime()
      const now = Date.now()
      return referenceTime <= now + PLANNED_ORDERS_PREVIEW_WINDOW_MS
    },
    getPlannedOrderReferenceDate(order) {
      const rawValue = order?.startAt
      if (!rawValue) {
        return null
      }

      const parsedDate = new Date(rawValue)
      if (Number.isNaN(parsedDate.getTime())) {
        return null
      }

      return parsedDate
    },
    openPlanningModal(teamDefinition) {
      if (this.$refs.orderPlanningModal) {
        this.$refs.orderPlanningModal.openForTeamDefinition(teamDefinition)
      }
    },
    handlePlanUpdated() {
      this.init()
    },
    hasPlannedOrders(team) {
      return this.getPlannedOrders(team).length > 0
    },
    getFirstPlannedOrderNumber(team) {
      if (!this.hasPlannedOrders(team)) return ''
      const first = this.getPlannedOrders(team)[0] || {}
      return first.fullNumber || '-'
    },
    getRemainingPlannedOrdersCount(team) {
      if (!this.hasPlannedOrders(team)) return 0
      return this.getPlannedOrders(team).length > 1 ? this.getPlannedOrders(team).length - 1 : 0
    },
    getPlannedOrders(team) {
      const teamDefinitionId = team?.teamDefinition?.id
      if (!teamDefinitionId) {
        return []
      }

      return this.plannedOrdersByTeamDefinition[teamDefinitionId] || []
    },
    transformDatetimeToHumanReadablePretty,
  },
}
</script>

<style lang="scss" >
@import "../../../assets/scss/variables/_variables.scss";

.team-card {
  &.team-card-success {
    border: 0 solid $success;
    border-left-width: 8px;
  }
  &.team-card-danger {
    border: 0 solid $danger;
    border-left-width: 8px;
  }
}
</style>
